import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCheck, UserX, UserPlus, Package, IndianRupee, Gift, Bike, Wallet, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { inr, inrCompact, num } from "@/lib/format";
import { KpiCard } from "@/components/KpiCard";
import { PageHeader } from "@/components/PageHeader";
import { ChartCard, AreaTrend, Bars, Donut, MultiLine } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";

export default function Dashboard() {
  const [kpi, setKpi] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tops, setTops] = useState([]);
  const [payOv, setPayOv] = useState(null);
  const [metric, setMetric] = useState("earnings");
  const nav = useNavigate();

  const load = () => {
    api.get("/dashboard/kpis").then(({ data }) => setKpi(data));
    api.get("/dashboard/charts").then(({ data }) => setCharts(data));
    api.get("/dashboard/payment-overview").then(({ data }) => setPayOv(data)).catch(() => setPayOv(null));
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("ct-refresh", h);
    return () => window.removeEventListener("ct-refresh", h);
  }, []);
  useEffect(() => { api.get(`/dashboard/top-performers?metric=${metric}&limit=6`).then(({ data }) => setTops(data)); }, [metric]);

  return (
    <div>
      <PageHeader title="Connect Tym Dashboard" subtitle="Real-time overview of delivery partners, orders, earnings & EV fleet">
        <Button variant="outline" onClick={() => nav("/operations")} data-testid="goto-operations-btn"><Zap /></Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpi && <>
          <KpiCard testId="kpi-total-dp" label="Total DP" value={num(kpi.total_dp)} icon={Users} accent="primary" delay={0} sub={`${kpi.new_onboarding_month} new this month`} />
          <KpiCard testId="kpi-active-dp" label="Active DP" value={num(kpi.active_dp)} icon={UserCheck} accent="emerald" delay={40} />
          <KpiCard testId="kpi-inactive-dp" label="Inactive DP" value={num(kpi.inactive_dp)} icon={UserX} accent="rose" delay={80} />
          <KpiCard testId="kpi-orders-today" label="Orders Today" value={num(kpi.orders_today)} icon={Package} accent="cyan" delay={120} sub={`${num(kpi.orders_week)} this week`} />
          <KpiCard testId="kpi-orders-month" label="Orders This Month" value={num(kpi.orders_month)} icon={TrendingUp} accent="violet" delay={160} />
          <KpiCard testId="kpi-earnings-today" label="DP Earnings Today" value={inrCompact(kpi.earnings_today)} icon={IndianRupee} accent="emerald" delay={200} />
          <KpiCard testId="kpi-earnings-month" label="DP Earnings Month" value={inrCompact(kpi.earnings_month)} icon={IndianRupee} accent="emerald" delay={240} sub={`${inrCompact(kpi.earnings_week)} this week`} />
          <KpiCard testId="kpi-incentives" label="Total Incentives" value={inrCompact(kpi.total_incentives)} icon={Gift} accent="amber" delay={280} />
          <KpiCard testId="kpi-ev-rent" label="EV On Rent" value={num(kpi.ev_on_rent)} icon={Bike} accent="cyan" delay={320} />
          <KpiCard testId="kpi-ev-collected" label="EV Rent Collected" value={inrCompact(kpi.ev_rent_collected)} icon={Wallet} accent="emerald" delay={360} />
          <KpiCard testId="kpi-ev-outstanding" label="Outstanding EV Rent" value={inrCompact(kpi.ev_rent_outstanding)} icon={Wallet} accent="rose" delay={400} />
          <KpiCard testId="kpi-new-onboarding" label="New Onboardings" value={num(kpi.new_onboarding_month)} icon={UserPlus} accent="violet" delay={440} sub="this month" />
        </>}
      </div>

      {payOv && (
        <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="dashboard-payment-overview">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-head text-sm font-semibold uppercase tracking-wide">Payment Overview</h3>
            <button onClick={() => nav("/payments")} className="text-xs text-primary hover:underline" data-testid="dashboard-view-payments">View all →</button>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <PayStat label="Pending Withdrawals" value={payOv.pending_count} sub={inr(payOv.pending_amount)} accent="amber" />
            <PayStat label="Paid Today" value={inrCompact(payOv.paid_today)} accent="emerald" />
            <PayStat label="Paid This Week" value={inrCompact(payOv.paid_week)} accent="cyan" />
            <PayStat label="Paid This Month" value={inrCompact(payOv.paid_month)} sub={`Total: ${inrCompact(payOv.paid_amount_total)}`} accent="primary" />
          </div>
        </div>
      )}

      {charts && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ChartCard title="Orders & Earnings Trend" subtitle="Last 30 days" className="lg:col-span-2">
            <MultiLine data={charts.daily} x="date" dualAxis lines={[{ key: "orders", name: "Orders" }, { key: "earnings", name: "Earnings ₹" }]} />
          </ChartCard>
          <ChartCard title="DP Status Split" subtitle="Active vs inactive">
            <Donut data={charts.active_inactive} />
          </ChartCard>
          <ChartCard title="Location Performance" subtitle="Orders by hub">
            <Bars data={charts.location} x="location" y="orders" color="#2563EB" />
          </ChartCard>
          <ChartCard title="Incentive Distribution" subtitle="By type">
            <Bars data={charts.incentives} x="type" y="amount" color="#F59E0B" />
          </ChartCard>
          <ChartCard title="Onboarding Trend" subtitle="New DPs by month">
            <AreaTrend data={charts.onboarding} x="month" y="count" color="#8B5CF6" />
          </ChartCard>
          <ChartCard title="Top 10 Delivery Partners" subtitle="By total earnings" className="lg:col-span-2">
            <Bars data={charts.top10.map((t) => ({ name: t.name, earnings: t.earnings }))} x="name" y="earnings" color="#10B981" horizontal height={300} />
          </ChartCard>
          <ChartCard title="Top Performers" subtitle="Leaderboard"
            action={<Select value={metric} onValueChange={setMetric}><SelectTrigger className="h-8 w-36" data-testid="leaderboard-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="earnings">Highest Earnings</SelectItem>
                <SelectItem value="orders">Highest Orders</SelectItem>
                <SelectItem value="active_days">Active Days</SelectItem>
                <SelectItem value="avg_orders">Avg Orders/Day</SelectItem>
              </SelectContent></Select>}>
            <div className="space-y-2">
              {tops.map((t) => (
                <button key={t.dp_id} onClick={() => nav(`/dp/${t.dp_id}`)} data-testid={`leaderboard-row-${t.rank}`}
                  className="flex w-full items-center gap-3 rounded-lg border border-border bg-secondary/40 p-2.5 text-left transition hover:border-primary/40 hover:bg-accent">
                  <span className={`flex h-8 w-8 items-center justify-center rounded-full font-head text-sm font-bold ${t.rank <= 3 ? "bg-amber-500/20 text-amber-500" : "bg-muted text-muted-foreground"}`}>{t.rank}</span>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{num(t.orders)} orders · {t.active_days} days</p></div>
                  <span className="tabular text-sm font-semibold text-emerald-500">{inrCompact(t.earnings)}</span>
                </button>
              ))}
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
}

function Zap() { return <span className="flex items-center gap-2 text-sm font-medium"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" /></svg>Daily Operations</span>; }

const PayStat = ({ label, value, sub, accent }) => (
  <div className="rounded-lg border border-border bg-secondary/40 p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className={`mt-1 font-head text-2xl font-bold tabular ${accent === "emerald" ? "text-emerald-500" : accent === "amber" ? "text-amber-500" : accent === "cyan" ? "text-cyan-500" : "text-primary"}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);
